import React, { useState, useContext, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";

import Header from "../../components/Header";

import { validateForm } from "../../utils/Validations";

import { EvaluateLoanFormType } from "../../types/evaluateLoanType";
import { ObjectType } from "../../types/tableTypes";
import { Clients, SizingTypeValue } from "../../types/enums";

import { RootContext } from "../../context/RootContext";
import { clickContinue } from "../../services/network";

import { mmtcBridgeGUCPropertyType, productTypeConfig } from "./evaluateConstant";
import { ELButtonGroup } from "./ELButtonGroup";

import "./evaluateLoan.css";
import { getCookie } from "../../utils/Common";

const initialDscrMMTCFormData = {
  productType: "",
  propertyType: "",
  sizingType: SizingTypeValue.DETAILED_LOAN_SIZING,
};

const initialBridgeMMTCFormData = {
  productType: "",
  loanType: "",
  propertyType: "",
  sizingType: SizingTypeValue.DETAILED_LOAN_SIZING,
};

const initialDscrFormData = {
  productType: "",
  propertyType: "",
};

const initialBridgeFormData = {
  productType: "",
  loanType: "",
  propertyType: "",
};

export default function EvaluateLoan() {
  const [formData, setFormData] = useState<EvaluateLoanFormType>(
    initialDscrFormData
  );
  const [selectedSubFields, setSelectedSubFields] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    setEvaluateLoanForm,
    userPermissions,
    dynamicConfig,
    setCurrentPath,
  } = useContext(RootContext);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const productOptions: ObjectType[] = useMemo(() => {
    const client =
      dynamicConfig?.ViteAppKcRealm === Clients.BPL_SIZER
        ? Clients.BPL_SIZER
        : Clients.MMTC;
    const data = productTypeConfig(userPermissions)?.filter((config: any) =>
      config.isVisible?.({ showSizingType: dynamicConfig?.showSizingType, realm: dynamicConfig?.ViteAppKcRealm, client })
    );
    return data;
  }, [userPermissions, dynamicConfig.ViteAppKcRealm, ]);

  useEffect(() => {
    setCurrentPath(window.location.pathname);
    if (!formData?.productType) return;

    const tempFormData: any = { ...formData };
    const selectedProductData = productOptions.find(
      (it) => it.key === formData?.productType
    );
    let subFieldsToBeChecked: any = selectedProductData?.subFields;
    const keys: string[] = subFieldsToBeChecked?.map(
      (field: any) => field?.key
    );
    keys?.forEach((objKey: string) => {
      tempFormData[objKey] = "";
    });

    if (formData?.productType === "investorDSCR") {
      delete tempFormData.loanType;
    }
    if (
      formData?.productType === "bridgeLoan" &&
      formData?.loanType === "groundUpText"
    ) {
      subFieldsToBeChecked = subFieldsToBeChecked.map((field: any) => {
        if (field.key === "propertyType") {
          return {
            ...field,
            fields: mmtcBridgeGUCPropertyType.fields.map((subField: any) => {
              return subField;
            }),
          };
        }
        return field;
      });
    }
    setSelectedSubFields(subFieldsToBeChecked);
  }, [productOptions, formData]);

  function handleChange(value: any, key: string) {
    let tempFormData: any = { ...formData };
    if (key === "productType") {
      switch (value) {
        case "bridgeLoan":
          tempFormData =
            dynamicConfig?.ViteAppKcRealm === Clients.BPL_SIZER
              ? initialBridgeFormData
              : initialBridgeMMTCFormData;
          break;
        case "investorDSCR":
          if (dynamicConfig?.ViteAppKcRealm === Clients.BPL_SIZER) {
            const hasChanged =
              JSON.stringify(tempFormData) !==
              JSON.stringify(initialDscrFormData);
            tempFormData = hasChanged
              ? initialDscrFormData
              : { ...formData };
          } else {
            tempFormData = initialDscrMMTCFormData;
          }
          break;
        default:
          break;
      }
    } else {
      tempFormData = { ...formData };
    }
    tempFormData[key] = value;
    setFormData(tempFormData);
  }

  async function handleContinue(e: React.FormEvent) {
    setIsLoading(true);
    try {
      e.preventDefault();
      if (validateForm(formData)) return;

      const reqBody: ObjectType = {};
      Object.keys(formData).forEach((key: string) => {
        const temp: ObjectType = { ...formData };
        reqBody[key] = t(temp[key]);
      });
      reqBody["orgId"] = getCookie("orgId");

      const { data } = await clickContinue(reqBody);
      sessionStorage.setItem(
        "evaluateLoanForm",
        JSON.stringify({ ...reqBody })
      );
      setEvaluateLoanForm(reqBody);

      let redirectUrl = `/loan/${reqBody.productType.replace(/ +/g, "")}`;
      if (reqBody?.loanType) {
        redirectUrl = `${redirectUrl}/${reqBody.loanType.replace(/ +/g, "")}`;
      }
      navigate(`${redirectUrl}/${data?.data?.loanId}`);
    } catch (err) {
      console.error(err, "API FAIL");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    // ✅ Full-viewport-height flex column so the page never collapses on sparse content
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        // ✅ Prevent any child from causing horizontal overflow
        overflowX: "hidden",
      }}
    >
      <Header showEvaluatePageBtn={true} data-testid="evaluate-header" />
      <Box
        component="main"
        sx={{
          flex: 1,
          py: { xs: 3, sm: 4, md: 5 },
          px: { xs: 2, sm: 4, md: 6, lg: 0 },
        }}
      >
        <Grid
          container
          // ✅ Centers the single content column at every breakpoint
          justifyContent="center"
        >
          <Grid
            item
            xs={12}
            sm={10}
            md={8}
            lg={6}
            xl={5}
          >
            <Typography
              component="h2"
              className="el-header"
              data-testid="evaluate-loan"
              sx={{
                fontSize: { xs: "1.25rem", sm: "1.4rem", md: "1.6rem", lg: "1.75rem" },
                textAlign: { xs: "center", sm: "left" },
                mb: { xs: 2, sm: 3 },
              }}
            >
              {t("evaluateLoanHeader")}
            </Typography>

            {/* Product Type button group */}
            <ELButtonGroup
              list={productOptions}
              groupLabel={"Product Type"}
              onClick={(e: any) => handleChange(e, "productType")}
              selectedValue={formData?.productType}
            />

            {/* Dynamic sub-field button groups */}
            {formData?.productType !== "" && selectedSubFields?.length ? (
              <>
                {selectedSubFields.map((subFieldData: ObjectType) => (
                  <ELButtonGroup
                    key={subFieldData.title}
                    list={subFieldData.fields}
                    groupLabel={subFieldData.title}
                    onClick={(e: any) => handleChange(e, subFieldData.key)}
                    selectedValue={
                      formData?.[
                        subFieldData?.key as keyof EvaluateLoanFormType
                      ]
                    }
                  />
                ))}
              </>
            ) : null}

            <LoadingButton
              disabled={validateForm(formData)}
              fullWidth
              variant="contained"
              type="submit"
              loading={isLoading}
              onClick={handleContinue}
              data-testid="continue-button"
              sx={{
                borderRadius: "6px",
                mt: { xs: 3, sm: 4 },
                fontSize: { xs: "14px", sm: "15px", md: "16px" },
                py: { xs: "10px", sm: "12px" },
                minHeight: { xs: "44px", sm: "48px" },
                color: "white",
              }}
            >
              {t("continue")}
            </LoadingButton>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}